import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Sparkles,
  Mail,
  Lock,
  User,
  Building,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  X,
  Gift
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCoreAuth } from '../contexts/CoreAuthContext';
import { supabase } from '../lib/supabase';
import { SubscriptionService } from '../lib/subscription-service';
import { createCheckoutSession, SUBSCRIPTION_PLANS, type PlanId } from '../lib/stripe-checkout';
import { OtpVerificationForm } from '../components/OtpVerificationForm';
import { TeamInvitationService } from '../lib/team-invitations';
import GTMService from '../lib/gtm';

interface Plan {
  id: PlanId;
  name: string;
  price: number;
  credits: number;
  features: string[];
  popular?: boolean;
  teamSeats: number | string;
  projects: number | string;
  versions: number | string;
}

const plans: Plan[] = [
  {
    id: 'trial',
    name: 'Free Trial',
    price: 0,
    credits: 3,
    teamSeats: 1,
    projects: 1,
    versions: 2,
    features: [
      '3 analyses per month',
      '1 team seat',
      '1 active project',
      '2 versions per creative',
      'Basic compliance checking',
      'Email support'
    ]
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 15.90,
    credits: 25,
    teamSeats: 1,
    projects: 3,
    versions: 15,
    features: [
      '25 analyses per month',
      '1 team seat',
      '3 active projects',
      '15 versions per creative',
      'All platform compliance',
      'Priority email support'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 39.90,
    credits: 100,
    teamSeats: 3,
    projects: 10,
    versions: 'Unlimited',
    popular: true,
    features: [
      '100 analyses per month',
      '3 team seats',
      '10 active projects',
      'Unlimited versions per creative',
      'Advanced analytics',
      'Performance insights',
      'Live chat support'
    ]
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 99.90,
    credits: 350,
    teamSeats: 10,
    projects: 25,
    versions: 'Unlimited',
    features: [
      '350 analyses per month',
      '10 team seats',
      '25 active projects',
      'Unlimited versions',
      'White-label reports',
      'API access',
      'Dedicated account manager'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299.90,
    credits: 1000,
    teamSeats: 'Unlimited',
    projects: 'Unlimited',
    versions: 'Unlimited',
    features: [
      '1,000 analyses per month',
      'Unlimited team seats',
      'Unlimited projects',
      'Unlimited versions',
      'Custom integrations',
      'SLA guarantees',
      '24/7 phone support'
    ]
  }
];

// Clean imports - no global declarations needed

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, resendEmailConfirmation, loginWithGoogle, hasSubscriptionPlan, updateSubscriptionPlan, user, isLoading } = useAuth();
  const { verifyOtp } = useCoreAuth();
  
  const [currentStep, setCurrentStep] = useState('auth'); // auth, otpVerification, emailConfirmation, subscription, dashboard
  const [authMode, setAuthMode] = useState('register'); // login, register
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanId | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  // OTP-related state
  const [otpType, setOtpType] = useState<'signup' | 'recovery' | 'email_change' | 'email'>('email');
  const [pendingOtpEmail, setPendingOtpEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Email confirmation screen OTP state
  const [otpCode, setOtpCode] = useState<string[]>(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<Plan[]>([]);
  const [showPlanDetails, setShowPlanDetails] = useState<{[key: string]: boolean}>({});
  // Invitation-related state
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company: '',
    referralCode: ''
  });
  
  // Terms and Privacy acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  // Marketing consent state
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Load subscription plans from database
  useEffect(() => {
    const loadSubscriptionPlans = async () => {
      try {
        setPlansLoading(true);
        const plans = await SubscriptionService.getAvailablePlans();
        setSubscriptionPlans(plans);
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        // Fallback to empty array so we use local plans
        setSubscriptionPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    loadSubscriptionPlans();
  }, []);

  // Check for invitation token and load invitation data
  useEffect(() => {
    const invitationTokenFromUrl = searchParams.get('invitation_token');
    const invitationTokenFromStorage = localStorage.getItem('rulevia_invitation_token');
    const invitationDataFromStorage = localStorage.getItem('rulevia_invitation_data');
    
    if (invitationTokenFromUrl || invitationTokenFromStorage) {
      const token = invitationTokenFromUrl || invitationTokenFromStorage;
      setInvitationToken(token);
      
      if (invitationDataFromStorage) {
        try {
          const data = JSON.parse(invitationDataFromStorage);
          setInvitationData(data);
        } catch (error) {
          console.error('Error parsing invitation data from storage:', error);
        }
      }
    }
  }, [searchParams]);

  // Set auth mode and step based on URL parameters
  useEffect(() => {
    const mode = searchParams.get('mode');
    const step = searchParams.get('step');
    const forceSubscription = searchParams.get('force') === 'subscription';
    const paymentStatus = searchParams.get('payment');
    const fromGoogle = searchParams.get('from') === 'google';
    
    if (mode === 'login' || mode === 'register') {
      setAuthMode(mode);
    }
    
    // **FIX: Handle email verification step properly to prevent loops**
    if (step === 'subscription') {
      // If user is already authenticated, go directly to subscription selection
      if (user && !isLoading) {
        setCurrentStep('subscription');
      } else {
        // User needs to sign in first
        setAuthMode('login');
        setCurrentStep('auth');
      }
      return; // Early return to prevent other logic from interfering
    }
    
    // Handle Google OAuth callback
    if (fromGoogle && user && !isLoading) {
      
      // Check if user has a subscription
      if (hasSubscriptionPlan()) {
        navigate('/dashboard');
      } else {
        setCurrentStep('subscription');
      }
      return; // Early return to prevent conflicts
    }
    
    // Force subscription selection (useful for failed payments or plan changes)
    if (forceSubscription && user && !isLoading) {
      setCurrentStep('subscription');
      
      // Handle payment cancellation messaging
      if (paymentStatus === 'cancelled') {
        setAuthError('Payment was cancelled. Please select a plan to continue or choose a different option.');
      }
      return; // Early return to prevent conflicts
    }

    // Check for forced plan selection via URL parameter
    const plan = searchParams.get('plan') as PlanId;
    const availablePlans = subscriptionPlans.length > 0 ? subscriptionPlans : plans;
    if (plan && availablePlans.find(p => p.id === plan)) {
      setSelectedPlan(plan);
    }

    // Check for referral code in URL parameter
    const referralCode = searchParams.get('ref');
    if (referralCode) {
      setFormData(prev => ({ ...prev, referralCode }));
      
      // Track referral link clicked
      GTMService.trackReferralClicked(referralCode);
    }
  }, [searchParams, user, isLoading, hasSubscriptionPlan, navigate, subscriptionPlans]);

  // Check user subscription status and direct to appropriate step
  useEffect(() => {
    if (!user || isLoading) return;

    const checkUserStatus = async () => {
      try {
        // **FIX: Don't interfere if URL has specific instructions**
        const step = searchParams.get('step');
        const forceSubscription = searchParams.get('force') === 'subscription';
        const fromGoogle = searchParams.get('from') === 'google';
        
        // If URL has specific navigation instructions, let the first useEffect handle it
        if (step === 'subscription' || forceSubscription || fromGoogle) {
          return;
        }
        
        // **NEW: Handle users with pending subscription_plan (partially completed onboarding)**
        // BUT first check if they are team members who should inherit organization subscription
        if (user.subscription_plan === null || user.subscription_plan === 'pending') {
          // Check if user is a team member first
          const isTeamMember = !!(user.organization_id && user.team_role);
          if (isTeamMember) {
            const returnTo = searchParams.get('returnTo');
            navigate(returnTo || '/dashboard');
            return;
          }
          
          setCurrentStep('subscription');
          return;
        }
        
        // Check if user has a valid subscription plan using hasSubscriptionPlan
        if (hasSubscriptionPlan()) {
          const returnTo = searchParams.get('returnTo');
          navigate(returnTo || '/dashboard');
          return;
        }

        // Check if user has an active subscription or canceled subscription still within period
        const { data: subscriptions } = await supabase
          .from('user_subscriptions')
          .select('status, plan_id, current_period_end, cancel_at_period_end')
          .eq('user_id', user.id)
          .in('status', ['active', 'canceled'])
          .order('created_at', { ascending: false })
          .limit(1);

        const subscription = subscriptions?.[0];

        // Check if user has access to their subscription
        if (subscription) {
          const now = new Date();
          const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
          
          // User has access if:
          // 1. Subscription is active, OR
          // 2. Subscription is canceled but still within the billing period
          const hasAccess = subscription.status === 'active' || 
                           (subscription.status === 'canceled' && periodEnd && now < periodEnd);
          
          if (hasAccess) {
            navigate('/dashboard');
            return;
          } else {
          }
        }

        // **FIX: Remove infinite reload loop - just clear payment session and continue**
        const paymentSessionId = searchParams.get('session_id') || localStorage.getItem('rulevia_payment_session_id');
        if (paymentSessionId) {
          // Clear the stored session to prevent future loops
          localStorage.removeItem('rulevia_payment_session_id');
          // Continue with normal onboarding flow instead of reloading
        }

        // Normal step management - show subscription selection if no active subscription
        if (!currentStep || currentStep === 'welcome') {
          setCurrentStep('subscription');
        }

      } catch (error) {
        console.error('Error checking user status:', error);
        // If there's an error, proceed with subscription selection
        if (!currentStep) {
          setCurrentStep('subscription');
        }
      }
    };

    checkUserStatus();
  }, [user, currentStep, navigate, searchParams, hasSubscriptionPlan, isLoading]);

  // Track when subscription plans are viewed
  useEffect(() => {
    if (currentStep === 'subscription' && user?.id) {
      GTMService.trackViewPlans(user.id);
    }
  }, [currentStep, user?.id]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (authError) {
      setAuthError(null);
    }
    if (termsError) {
      setTermsError(null);
    }
  };

  const clearResendState = () => {
    setResendError(null);
    setResendSuccess(false);
    setResendLoading(false);
  };

  // Utility function to generate subscription selection URLs
  const generateSubscriptionURL = (reason?: 'cancelled' | 'failed' | 'expired') => {
    const params = new URLSearchParams({
      force: 'subscription'
    });
    
    if (reason) {
      params.set('payment', reason);
    }
    
    return `${window.location.origin}/onboarding?${params.toString()}`;
  };

  const handleAuth = async () => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      if (authMode === 'login') {
        await login(formData.email, formData.password);
        
        // Track successful login
        GTMService.trackLogin('email', user?.id);
        
        // After successful login, don't immediately check subscription
        // Let the useEffect hooks handle the navigation after profile loads
      } else {
        // For registration
        if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
          throw new Error('Please fill in all required fields');
        }
        
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }
        
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        if (!termsAccepted) {
          setTermsError('You must accept the Terms of Service and Privacy Policy to continue');
          return;
        }
        
        // Register and go to email confirmation screen (which now has OTP input)
        await register(formData.email, formData.password, formData.name, formData.referralCode || undefined, termsAccepted, marketingConsent);
        
        // Track successful registration
        GTMService.trackSignUp('email', user?.id);
        
        setCurrentStep('emailConfirmation');
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      
      // Handle specific authentication error messages
      let errorMessage = error.message || 'Authentication failed';
      
      if (error?.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (error?.message?.includes('Email not confirmed') || 
                 error?.message?.includes('verify your email address') ||
                 error?.message?.includes('email must be confirmed')) {
        // For email confirmation issues during login, show option to resend
        errorMessage = 'Your email address needs to be verified. Please check your email for the verification link.';
        // Switch to email confirmation screen to allow resending
        setCurrentStep('emailConfirmation');
        setAuthError(null); // Clear the error since we're moving to confirmation screen
        return;
      } else if (error?.message?.includes('User already registered') || 
                 error?.message?.includes('already exists')) {
        // Handle duplicate email - automatically switch to login mode
        if (authMode === 'register') {
          setAuthMode('login');
          errorMessage = 'This email is already registered. Please sign in to continue.';
        } else {
          errorMessage = 'An account with this email already exists. Try signing in instead.';
        }
      } else if (error?.message?.includes('Password should be at least 6 characters')) {
        errorMessage = 'Password must be at least 6 characters long.';
      } else if (error?.message?.includes('Unable to validate email address')) {
        errorMessage = 'Please enter a valid email address.';
      }
      
      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      await loginWithGoogle();
      
      // Track Google login (user ID will be available after redirect)
      GTMService.trackLogin('google');
      
      // User will be redirected to Google, no need for further action here
    } catch (error: any) {
      console.error('Google login error:', error);
      setAuthError('Failed to connect with Google. Please try again.');
      setAuthLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!formData.email) {
      setResendError('Email address is required');
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendSuccess(false);
    
    try {
      await resendEmailConfirmation(formData.email);
      setResendSuccess(true);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Resend email error:', error);
      
      // Handle specific error messages
      if (error.message?.includes('rate limit')) {
        setResendError('Too many requests. Please wait a few minutes before trying again.');
      } else if (error.message?.includes('email not found')) {
        setResendError('This email address was not found. Please check your email or register again.');
      } else if (error.message?.includes('already confirmed')) {
        setResendError('Your email is already confirmed. You can sign in now.');
      } else {
        setResendError(error.message || 'Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  // OTP Handler Functions
  const handleOtpVerify = async (token: string) => {
    setAuthError(null);
    
    try {
      await verifyOtp(pendingOtpEmail, token, otpType);
      
      // After successful OTP verification, redirect appropriately
      if (otpType === 'signup') {
        // Signup OTP verified - proceed to subscription
        setCurrentStep('subscription');
        return;
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      throw new Error(error.message || 'Invalid verification code. Please try again.');
    }
  };

  const handleOtpResend = async () => {
    try {
      if (otpType === 'signup') {
        // For signup, resend the signup confirmation email (this will include the OTP)
        await resendEmailConfirmation(pendingOtpEmail);
      }
      // Note: Other OTP types (recovery, etc.) are handled in their respective pages
    } catch (error: any) {
      console.error('OTP resend error:', error);
      throw new Error(error.message || 'Failed to resend verification code. Please try again.');
    }
  };


  // Email confirmation screen OTP handlers
  const handleOtpInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...otpCode];
    newCode[index] = value.slice(-1); // Only take the last digit
    setOtpCode(newCode);
    setOtpError(null); // Clear error when user starts typing

    // Auto-advance to next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleEmailConfirmationOtpVerify(newCode.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setOtpCode(newCode);
      otpInputRefs.current[5]?.focus();
      handleEmailConfirmationOtpVerify(pastedData);
    }
  };

  const handleEmailConfirmationOtpVerify = async (token: string) => {
    if (token.length !== 6) return;
    
    setIsVerifyingOtp(true);
    setOtpError(null);
    
    try {
      await verifyOtp(formData.email, token, 'signup');
      
      // After successful verification, proceed to subscription selection
      setCurrentStep('subscription');
    } catch (error: any) {
      console.error('Email confirmation OTP verification error:', error);
      setOtpError(error.message || 'Invalid verification code. Please try again.');
      // Clear the code inputs
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsVerifyingOtp(false);
    }
  };


  const handleSubscription = async () => {
    try {
      // Clear any previous errors
      setAuthError(null);
      
      // Check if user has a valid session and email is verified
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.user && session.user.email_confirmed_at) {
        // User is properly authenticated and email verified
        
        if (selectedPlan === 'trial') {
          // Track trial selection
          GTMService.trackBeginCheckout('Free Trial', 0, user?.id);
          
          // Free trial - create subscription directly
          await SubscriptionService.createSubscription({
            userId: session.user.id,
            planId: selectedPlan,
            currentPeriodStart: new Date(),
            currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
          });
          
          // Update subscription plan in auth context
          await updateSubscriptionPlan(selectedPlan);
          
          // Track trial "purchase" (free)
          GTMService.trackPurchase(
            `trial_${session.user.id}_${Date.now()}`,
            'Free Trial',
            0,
            user?.id,
            'new'
          );
          
          // Note: Invitation is already handled in InvitationAcceptancePage
          // This user is coming from successful invitation acceptance
          
          // Redirect to returnTo path or dashboard
          const returnTo = searchParams.get('returnTo');
          const redirectPath = returnTo ? decodeURIComponent(returnTo) : '/dashboard';
          navigate(redirectPath);
        } else {
          // Paid plan - use new checkout session approach
          try {
            // Find the selected plan details
            const selectedPlanData = SUBSCRIPTION_PLANS[selectedPlan as keyof typeof SUBSCRIPTION_PLANS];
            if (!selectedPlanData) {
              throw new Error(`Plan '${selectedPlan}' not found`);
            }
            
            if (!selectedPlanData.priceId) {
              throw new Error('Price ID not found for plan');
            }
            
            
            // Track begin checkout for paid plans
            GTMService.trackBeginCheckout(selectedPlanData.name, selectedPlanData.price, user?.id);
            
            const result = await createCheckoutSession({
              userId: session.user.id,
              priceId: selectedPlanData.priceId,
              planName: selectedPlanData.name,
              userEmail: session.user.email || undefined
            });
            
            if (result.url) {
              // Redirect to Stripe checkout
              window.location.href = result.url;
            } else {
              throw new Error('Failed to create checkout session');
            }
          } catch (checkoutError) {
            console.error('Failed to create checkout session:', checkoutError);
            setAuthError(`Failed to create payment session: ${checkoutError instanceof Error ? checkoutError.message : 'Unknown error'}. Please try again or contact support.`);
          }
        }
      } else if (session && session.user && !session.user.email_confirmed_at) {
        // User is signed in but email not verified
        setAuthError('Please verify your email address before selecting a subscription plan.');
        setCurrentStep('emailConfirmation');
      } else {
        // No session found - user needs to sign in first
        setAuthError('Your session has expired. Please sign in again to continue.');
        setCurrentStep('auth');
        setAuthMode('login');
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      setAuthError('An unexpected error occurred. Please try again or contact support if the problem persists.');
    }
  };

  // Function to handle direct plan selection and subscription
  const handlePlanSelection = async (planId: string) => {
    try {
      // First set the selected plan
      setSelectedPlan(planId as PlanId);
      
      // Clear any previous errors
      setAuthError(null);
      
      // Check if user has a valid session and email is verified
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.user && session.user.email_confirmed_at) {
        // User is properly authenticated and email verified
        
        if (planId === 'trial') {
          // Free trial - create subscription directly
          await SubscriptionService.createSubscription({
            userId: session.user.id,
            planId: planId,
            currentPeriodStart: new Date(),
            currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
          });
          
          // Update subscription plan in auth context
          await updateSubscriptionPlan(planId);
          
          // Note: Invitation is already handled in InvitationAcceptancePage
          // This user is coming from successful invitation acceptance
          
          // Redirect to returnTo path or dashboard for free trial
          const returnTo = searchParams.get('returnTo');
          const redirectPath = returnTo ? decodeURIComponent(returnTo) : '/dashboard';
          navigate(redirectPath);
        } else {
          // Paid plan - use new checkout session approach
          try {
            // Find the selected plan details
            const selectedPlanData = SUBSCRIPTION_PLANS[planId as keyof typeof SUBSCRIPTION_PLANS];
            if (!selectedPlanData) {
              throw new Error(`Plan '${planId}' not found`);
            }
            
            if (!selectedPlanData.priceId) {
              throw new Error('Price ID not found for plan');
            }
            
            
            // Track begin checkout for standalone plan selection
            GTMService.trackBeginCheckout(selectedPlanData.name, selectedPlanData.price, user?.id);
            
            const result = await createCheckoutSession({
              userId: session.user.id,
              priceId: selectedPlanData.priceId,
              planName: selectedPlanData.name,
              userEmail: session.user.email || undefined
            });
            
            if (result.url) {
              // Redirect to Stripe checkout
              window.location.href = result.url;
            } else {
              throw new Error('Failed to create checkout session');
            }
          } catch (checkoutError) {
            console.error('Failed to create checkout session for plan selection:', checkoutError);
            setAuthError(`Failed to create payment session: ${checkoutError instanceof Error ? checkoutError.message : 'Unknown error'}. Please try again or contact support.`);
          }
        }
      } else {
        // User not authenticated - show sign in modal or redirect to auth
        setAuthError('Please sign in first to select a plan.');
        setCurrentStep('auth');
      }
    } catch (error) {
      console.error('Error with plan selection:', error);
      setAuthError(`Failed to process plan selection: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getDisplayPrice = (plan: any) => {
    // Handle both local plans (price) and database plans (priceMonthly in cents)
    const price = plan.price ?? (plan.priceMonthly ? plan.priceMonthly / 100 : 0);
    if (price === 0) return '0';
    return price.toFixed(2);
  };

  const getDisplayPeriod = (plan: any) => {
    // Handle both local plans (price) and database plans (priceMonthly in cents)
    const price = plan.price ?? (plan.priceMonthly ? plan.priceMonthly / 100 : 0);
    if (price === 0) return '';
    return 'month';
  };

  const formatLimit = (limit: number | string | undefined): string => {
    if (limit === undefined || limit === null) return '∞';
    if (limit === -1 || limit === 'Unlimited') return 'Unlimited';
    return limit.toString();
  };

  const getPlanBadge = (plan: any): string | null => {
    if (plan.id === 'trial') return 'Try Free';
    if (plan.popular) return 'Most Popular';
    if (plan.id === 'agency') return 'Best Value';
    if (plan.id === 'enterprise') return 'Enterprise';
    return null;
  };

  const getPlanColor = (plan: any): string => {
    switch (plan.id) {
      case 'trial': return 'from-gray-500 to-gray-600';
      case 'starter': return 'from-blue-500 to-blue-600';
      case 'pro': return 'from-purple-500 to-blue-500';
      case 'agency': return 'from-yellow-500 to-orange-500';
      case 'enterprise': return 'from-purple-600 to-indigo-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Get key features (most important, always shown)
  const getKeyFeatures = (plan: any): string[] => {
    // Handle both local plans (credits) and database plans (monthlyCredits)
    const credits = plan.credits ?? plan.monthlyCredits ?? 0;
    const baseFeatures = [
      `${credits} analyses/month`,
      'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
      `${formatLimit(plan.teamSeats)} team member${plan.teamSeats === 1 ? '' : 's'}`,
      `${formatLimit(plan.projects)} project${plan.projects === 1 ? '' : 's'}`
    ];
    
    
    return baseFeatures;
  };

  // Get detailed features (less important, shown on expand)
  const getDetailedFeatures = (plan: any): string[] => {
    const features = plan.features || [];
    const keyFeatureTexts = [
      'analyses per month',
      'team member',
      'active project',
      'platforms',
      'meta',
      'google',
      'tiktok',
      'youtube',
      'pinterest',
      'microsoft'
    ];
    
    // Filter out features that are already shown in key features
    return features.filter((feature: string) => 
      !keyFeatureTexts.some(keyText => feature.toLowerCase().includes(keyText))
    );
  };

  const togglePlanDetails = (planId: string) => {
    setShowPlanDetails(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const renderAuthScreen = () => {
    if (authMode === 'register') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl mx-4 sm:mx-0 text-center"
          >
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-6">
              <img src="/Logo-checkmark-sidebar.svg" alt="Rulevia Logo" className="w-12 h-12" />
            </div>
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Registrations Closed</h2>
            <p className="text-gray-600 text-sm mb-6">
              Rulevia is shutting down on <strong>July 14, 2026</strong>. New registrations are no longer accepted. Thank you for your interest.
            </p>
            <button
              onClick={() => setAuthMode('login')}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium underline"
            >
              Already have an account? Sign in
            </button>
          </motion.div>
        </div>
      );
    }

    return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl mx-4 sm:mx-0"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
            <img
              src="/Logo-checkmark-sidebar.svg"
              alt="Rulevia Logo"
              className="w-12 h-12"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {authMode === 'register' ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            {authMode === 'register' 
              ? 'Start your free trial today' 
              : 'Sign in to your Rulevia account'
            }
          </p>
        </div>

        <div className="space-y-4">
          {authMode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] text-base"
                  placeholder="Enter your full name"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] text-base"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {authMode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] text-base"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {authMode === 'register' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company (Optional)
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] text-base"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referral Code (Optional)
                </label>
                <div className="relative">
                  <Gift className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.referralCode}
                    onChange={(e) => handleInputChange('referralCode', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] text-base"
                    placeholder="Enter referral code"
                  />
                </div>
              </div>
            </>
          )}

          {/* Terms and Privacy Acceptance - Only for Registration */}
          {authMode === 'register' && (
            <div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5 mt-1">
                  <input
                    id="terms-acceptance"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) {
                        setTermsError(null);
                      }
                    }}
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                  />
                </div>
                <label htmlFor="terms-acceptance" className="text-sm text-gray-700 leading-relaxed">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/terms', '_blank')}
                    className="text-purple-600 hover:text-purple-700 underline font-medium"
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/privacy', '_blank')}
                    className="text-purple-600 hover:text-purple-700 underline font-medium"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>
              
              {termsError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-start space-x-2"
                >
                  <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-700">{termsError}</span>
                </motion.div>
              )}
            </div>
          )}

          {/* Marketing Consent - Only for Registration */}
          {authMode === 'register' && (
            <div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5 mt-1">
                  <input
                    id="marketing-consent"
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                  />
                </div>
                <label htmlFor="marketing-consent" className="text-sm text-gray-700 leading-relaxed">
                  I want to receive product updates, tips, and special offers via email (optional)
                </label>
              </div>
            </div>
          )}

          {/* Error/Info Message */}
          {authError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${
                authError.includes('Almost there') || authError.includes('check your email')
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-red-50 border border-red-200'
              } rounded-lg p-3 flex items-start space-x-2`}
            >
              {authError.includes('Almost there') || authError.includes('check your email') ? (
                <Mail className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <span className={`text-sm ${
                authError.includes('Almost there') || authError.includes('check your email')
                  ? 'text-blue-700' 
                  : 'text-red-700'
              }`}>
                {authError}
              </span>
            </motion.div>
          )}


          <button
            onClick={handleAuth}
            disabled={authLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px] text-base"
          >
            {authLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{authMode === 'register' ? 'Creating Account...' : 'Signing In...'}</span>
              </div>
            ) : (
              <span>{authMode === 'register' ? 'Create Account' : 'Sign In'}</span>
            )}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <button 
            onClick={handleGoogleLogin}
            disabled={authLoading}
            className="w-full flex items-center justify-center space-x-2 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] text-base"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{authLoading ? 'Connecting...' : 'Continue with Google'}</span>
          </button>


          {authMode === 'login' && (
            <div className="text-center">
              <button 
                onClick={() => navigate('/forgot-password')}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium min-h-[44px] py-2 px-4">
                Forgot your password?
              </button>
            </div>
          )}

          <div className="text-center">
            <span className="text-gray-600 text-sm">
              {authMode === 'register' ? 'Already have an account?' : "Don't have an account?"}
            </span>
            <button
              onClick={() => {
                setAuthMode(authMode === 'register' ? 'login' : 'register');
                setAuthError(null);
              }}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium ml-1 min-h-[44px] py-2 px-2"
            >
              {authMode === 'register' ? 'Sign in' : 'Sign up'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
  };

  const renderSubscriptionScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">Rulevia</span>
          </div>
          
          <div className="flex items-center space-x-3">
            {user?.email && (
              <span className="text-sm text-gray-600">
                Signed in as {user.email}
              </span>
            )}
            <button
              onClick={() => {
                // Clear any errors and go back to auth
                setAuthError(null);
                setCurrentStep('auth');
                setAuthMode('login');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] py-2 px-4"
            >
              ← Back to Sign In
            </button>
          </div>
        </div>

        {/* Error Display */}
        {authError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 max-w-2xl mx-auto"
          >
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-700 font-medium">Payment Error</p>
                  <p className="text-sm text-red-600">{authError}</p>
                  <p className="text-xs text-red-500 mt-1">
                    Please select a plan again or try a different payment method.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="text-center mb-12">
          {invitationData || searchParams.get('invitation') === 'accepted' ? (
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to the Team! 🎉</h1>
              <p className="text-xl text-gray-600 mb-4">
                {invitationData ? (
                  <>You're joining <span className="font-semibold text-purple-600">{invitationData.organizationName}</span> as a <span className="font-semibold">{invitationData.role}</span></>
                ) : (
                  <>Your invitation has been accepted! Now choose your subscription plan.</>
                )}
              </p>
              <p className="text-lg text-gray-500 mb-8">
                Select a plan to complete your setup and start collaborating with your team
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
              <p className="text-xl text-gray-600 mb-8">
                Start with a free trial, then select the plan that fits your needs
              </p>
            </>
          )}
        </div>

        {plansLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Loading plans...</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-8 sm:mb-12">
              {(subscriptionPlans.length > 0 ? subscriptionPlans : plans).map((plan, index) => {
                const displayPrice = getDisplayPrice(plan);
                const displayPeriod = getDisplayPeriod(plan);
                const planBadge = getPlanBadge(plan);
                const planColor = getPlanColor(plan);
                const keyFeatures = getKeyFeatures(plan);
                const detailedFeatures = getDetailedFeatures(plan);
                const isExpanded = showPlanDetails[plan.id] || false;
                
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative bg-white rounded-2xl p-4 sm:p-6 border-2 transition-all hover:shadow-xl cursor-pointer ${
                      selectedPlan === plan.id
                        ? 'border-purple-500 shadow-xl bg-gradient-to-b from-purple-50 to-white'
                        : 'border-gray-200 hover:border-purple-300'
                    } ${plan.popular ? 'ring-2 ring-purple-200 sm:transform sm:scale-105' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {planBadge && (
                      <div className={`absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 px-3 sm:px-4 py-1 rounded-full text-xs sm:text-xs font-medium text-white shadow-lg ${
                        plan.popular ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 
                        plan.id === 'trial' ? 'bg-gradient-to-r from-gray-500 to-gray-600' :
                        plan.id === 'enterprise' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
                        'bg-gradient-to-r from-orange-500 to-yellow-500'
                      }`}>
                        {planBadge}
                      </div>
                    )}
                    
                    {/* Header */}
                    <div className="text-center mb-4 sm:mb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{plan.name}</h3>
                      <div className="mb-3 sm:mb-4">
                        <div className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${planColor} bg-clip-text text-transparent`}>
                          ${displayPrice}
                        </div>
                        {displayPeriod && <div className="text-gray-500 text-xs sm:text-sm">per {displayPeriod}</div>}
                      </div>
                    </div>

                    {/* Key Features - Always Visible */}
                    <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                      {keyFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded-full bg-gradient-to-r ${planColor} flex items-center justify-center flex-shrink-0`}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-xs sm:text-sm font-medium text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Show More/Less Button */}
                    {detailedFeatures.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlanDetails(plan.id);
                        }}
                        className="w-full py-3 sm:py-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors border-t border-gray-100 mb-3 sm:mb-4 min-h-[44px] sm:min-h-auto flex items-center justify-center"
                      >
                        {isExpanded ? 'Show Less' : `Show ${detailedFeatures.length} More Features`}
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="inline-block ml-1"
                        >
                          ↓
                        </motion.div>
                      </button>
                    )}

                    {/* Detailed Features - Expandable */}
                    <motion.div
                      initial={false}
                      animate={{ 
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      {isExpanded && (
                        <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4 pt-2 border-t border-gray-100">
                          {detailedFeatures.map((feature, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-1" />
                              <span className="text-xs sm:text-xs text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>

                    {/* Action Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanSelection(plan.id);
                      }}
                      className={`w-full py-3 sm:py-3 px-3 sm:px-4 rounded-xl text-sm sm:text-sm font-semibold transition-all duration-200 min-h-[44px] flex items-center justify-center ${
                        selectedPlan === plan.id
                          ? `bg-gradient-to-r ${planColor} text-white shadow-lg transform hover:scale-105 hover:shadow-xl`
                          : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      {plan.id === 'trial' ? 'Start Free Trial' : 'Select Plan'}
                    </button>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center">
              <button
                onClick={handleSubscription}
                disabled={plansLoading}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 flex items-center justify-center mx-auto group disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
              >
                Continue to Rulevia
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center px-4">
                You can change or cancel your plan anytime. Your credits refresh every month.
              </p>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );

  const renderGoogleLoadingScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl text-center mx-4 sm:mx-0"
      >
        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Completing Sign In
        </h2>
        
        <p className="text-gray-600 mb-6">
          Please wait while we complete your Google sign in...
        </p>
        
        <div className="flex items-center justify-center space-x-2 text-purple-600">
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="text-sm font-medium">Signing in with Google</span>
        </div>
      </motion.div>
    </div>
  );

  const renderOtpVerificationScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20">
          <OtpVerificationForm
            email={pendingOtpEmail}
            onVerify={handleOtpVerify}
            onResend={handleOtpResend}
            isLoading={authLoading}
            error={authError}
            type={otpType}
            title={
              otpType === 'email' && authMode === 'passwordlessLogin'
                ? 'Sign In to Your Account'
                : otpType === 'signup'
                ? 'Complete Your Registration'
                : 'Verify Your Email'
            }
            subtitle={
              otpType === 'email' && authMode === 'passwordlessLogin'
                ? 'Enter the code sent to your email to sign in'
                : otpType === 'signup'
                ? 'Enter the code sent to your email to verify your account'
                : 'Enter the verification code sent to your email'
            }
          />
          
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setCurrentStep('auth');
                setAuthError(null);
                setPendingOtpEmail('');
              }}
              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const renderEmailConfirmationScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl text-center mx-4 sm:mx-0"
      >
        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-white" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {invitationData ? 'Complete Your Invitation' : 'Check Your Email'}
        </h2>
        
        {invitationData ? (
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              We've sent a verification link to <strong>{formData.email || invitationData.email}</strong>. 
              Please click the link or enter the 6-digit code below to verify your account and join <span className="font-semibold text-purple-600">{invitationData.organizationName}</span>.
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-purple-700">
                👥 You're joining as a <strong>{invitationData.role}</strong> - almost done!
              </p>
            </div>
          </div>
        ) : (
          <p className="text-gray-600 mb-6">
            We've sent a verification link to <strong>{formData.email}</strong>. 
            Please click the link in your email to verify your account, then come back to sign in.
          </p>
        )}
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5">
              ℹ️
            </div>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Next steps:</p>
              <ol className="list-decimal list-inside space-y-1 text-left">
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the verification link OR enter the 6-digit code below</li>
                <li>Choose your subscription plan</li>
              </ol>
            </div>
          </div>
        </div>

        {/* OTP Input Section */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="text-sm text-gray-500 font-medium">Or enter verification code</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>
          
          <div className="flex justify-center space-x-2 mb-4">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={el => {
                  if (!otpInputRefs.current) otpInputRefs.current = [];
                  otpInputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                value={otpCode[index] || ''}
                onChange={(e) => handleOtpInputChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
                className={`w-10 h-10 sm:w-10 sm:h-12 text-center text-base sm:text-lg font-bold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  otpError 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                } ${otpCode[index] ? 'bg-purple-50 border-purple-300' : 'bg-white'}`}
                maxLength={1}
                disabled={isVerifyingOtp}
              />
            ))}
          </div>

          {otpError && (
            <div className="text-center mb-4">
              <p className="text-sm text-red-600">{otpError}</p>
            </div>
          )}

          {isVerifyingOtp && (
            <div className="flex items-center justify-center space-x-2 text-gray-600 text-sm mb-4">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Verifying code...</span>
            </div>
          )}
        </div>

        {/* Success message for resend */}
        {resendSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4"
          >
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-500" />
              <p className="text-sm text-green-700 font-medium">
                Verification email sent successfully! Please check your inbox.
              </p>
            </div>
          </motion.div>
        )}

        {/* Error message for resend */}
        {resendError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
          >
            <div className="flex items-center space-x-2">
              <X className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700">{resendError}</p>
            </div>
          </motion.div>
        )}

        {/* Resend email button */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-3">
            Didn't receive the email?
          </p>
          <button
            onClick={handleResendEmail}
            disabled={resendLoading || resendSuccess}
            className={`w-full py-3 rounded-lg font-medium transition-all min-h-[44px] text-base ${
              resendLoading || resendSuccess
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {resendLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </div>
            ) : resendSuccess ? (
              <div className="flex items-center justify-center space-x-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Email sent!</span>
              </div>
            ) : (
              'Resend verification email'
            )}
          </button>
        </div>
        
        <button
          onClick={() => {
            setCurrentStep('auth');
            setAuthMode('login');
            setAuthError(null);
            clearResendState();
          }}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 mb-4 min-h-[44px] text-base"
        >
          Continue to Sign In
        </button>
        
        <button
          onClick={() => {
            setCurrentStep('auth');
            clearResendState();
          }}
          className="text-purple-600 hover:text-purple-700 text-sm font-medium min-h-[44px] py-2 px-4"
        >
          Back to Registration
        </button>
      </motion.div>
    </div>
  );

  // Check if we should show Google loading screen
  const fromGoogle = searchParams.get('from') === 'google';
  const shouldShowGoogleLoading = fromGoogle && (isLoading || (!user && currentStep === 'auth'));

  return (
    <div>
      <Helmet>
        <title>Sign Up / Login - Rulevia</title>
      </Helmet>
      {shouldShowGoogleLoading && renderGoogleLoadingScreen()}
      {!shouldShowGoogleLoading && currentStep === 'auth' && renderAuthScreen()}
      {!shouldShowGoogleLoading && currentStep === 'otpVerification' && renderOtpVerificationScreen()}
      {!shouldShowGoogleLoading && currentStep === 'emailConfirmation' && renderEmailConfirmationScreen()}
      {!shouldShowGoogleLoading && currentStep === 'subscription' && renderSubscriptionScreen()}
    </div>
  );
}